import { DnDArea, UploadLabel } from "components/design/UploadFileSection";
import { openDownloadUrl } from "util/openDownloadUrl";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { ArrowDown } from "components/icons/ArrowDown";
import { BankLogo } from "components/icons/BankLogo";
import { DownloadIcon } from "components/icons/Download";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { useLazyGetTransactionBulkTemplateQuery } from "store/apis/transactions";
import { Transaction } from "types/Models/books";
import { UPLOAD_TXN_SAMPLE_FILE } from "types/contants/sampleExcelSheet";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { openLink } from "utils/openLink";
import { TransactionsBankSelector } from "./TransactionsBankSelector";

type AddTransactionOptionsProps = ModalProps & {
  openAddTransactionModal: () => void;
  selectedAccountId?: string;
  defaultTransaction?: Transaction;
};

export const AddBulkTransaction = ({
  isOpen,
  close,
  openAddTransactionModal,
  selectedAccountId,
  defaultTransaction,
}: AddTransactionOptionsProps) => {
  const [csv, setCsv] = useState<File | undefined>(undefined);
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast } = useToast();
  const [fetchTransactionTemplate, { isLoading: isTemplateDownloading }] =
    useLazyGetTransactionBulkTemplateQuery();

  const handleCSVTemplateDownload = () => {
    openLink(UPLOAD_TXN_SAMPLE_FILE);
  };

  const handleExcelTemplateDownload = async () => {
    try {
      const { download_url } = await fetchTransactionTemplate({
        entityId,
      }).unwrap();
      openDownloadUrl(download_url);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const {
    close: closeBankSelectorModal,
    isOpen: isBankSelectorModalOpen,
    open: openBankSelectorModal,
  } = useModal();

  const hasDefault = Boolean(defaultTransaction);

  const addtransactionManually = useModal();

  const { data: banks } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const selectedAccount = banks?.accounts.find(
    ({ account }) => account.uuid === selectedAccountId
  );

  const source = `${selectedAccount?.account.nickname} •••• ${selectedAccount?.account.mask}`;

  const onDrop = async (files: File[]) => {
    setCsv(files[0]);
    openTxnBankSelectorModal();
  };

  const openTxnBankSelectorModal = () => {
    if (selectedAccountId) {
      addtransactionManually.open();
    }
    openBankSelectorModal();
    close();
  };

  const onBankSelectorModalClose = () => {
    setCsv(undefined);
    closeBankSelectorModal();
  };

  const onBack = () => {
    onBankSelectorModalClose();
    openAddTransactionModal();
  };

  if (hasDefault) {
    return (
      <TransactionsBankSelector
        isOpen={hasDefault}
        close={close}
        onBack={onBack}
        csv={csv}
        step={1}
        defaultTransaction={defaultTransaction}
      />
    );
  }

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content size="large">
          <Modal.Header className="sm:t-pl-4">
            <div className="t-w-full">
              <Modal.Title>Add transactions</Modal.Title>
              {selectedAccountId && (
                <Modal.Subtitle>
                  <div className="t-flex t-gap-2 t-items-center t-text-subtext t-text-text-60">
                    <BankLogo size={20} />
                    SOURCE: {source}
                  </div>
                </Modal.Subtitle>
              )}
            </div>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-pb-10 t-flex t-gap-4 t-flex-col sm:t-px-4 sm:t-pb-6">
            <UploadLabel>
              Download the pre-formatted template to ensure your data is
              properly structured. Add the transaction details to the template
              and upload it.
            </UploadLabel>
            <Dropdown.Root>
              <Dropdown.Trigger asChild>
                <div>
                  <Button size="small" isLoading={isTemplateDownloading}>
                    <div className="t-flex t-items-center t-gap-1">
                      <DownloadIcon />
                      <span className="t-ml-1.5">Download template</span>
                      <ArrowDown color="currentColor" strokeWidth="1.6" />
                    </div>
                  </Button>
                </div>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content sideOffset={4} align="start">
                  <Dropdown.Item onSelect={handleCSVTemplateDownload}>
                    Download CSV
                  </Dropdown.Item>
                  <Dropdown.Item onSelect={handleExcelTemplateDownload}>
                    Download Excel (.xlsx)
                  </Dropdown.Item>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
            <DnDArea
              onDrop={onDrop}
              accept={{
                "text/csv": [".csv"],
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [".xlsx"],
              }}
              multiple={false}
              subText="File Format: csv / xlsx"
            />
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
      <TransactionsBankSelector
        isOpen={isBankSelectorModalOpen}
        close={onBankSelectorModalClose}
        onBack={onBack}
        csv={csv}
        step={addtransactionManually.isOpen ? 2 : 1}
      />
    </>
  );
};
